import { useEffect, useMemo, useState } from "react";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import EmptyState from "@/components/base/EmptyState";
import DataTable, { type DataColumn } from "@/components/base/DataTable";
import { useAuth } from "@/context/AuthContext";
import { getOwnerLeads, updateOwnerLeadStatus } from "@/services/ownerApi";
import type { OwnerLead } from "@/types";

const statusTabs: { value: "all" | OwnerLead["status"]; label: string }[] = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

const statusOptions: OwnerLead["status"][] = ["new", "contacted", "qualified", "won", "lost"];

const sourceMeta: Record<OwnerLead["source"], { icon: string; label: string }> = {
  listing: { icon: "ri-mail-send-line", label: "Enquiry form" },
  call: { icon: "ri-phone-line", label: "Click-to-call" },
  whatsapp: { icon: "ri-whatsapp-line", label: "WhatsApp" },
  profile: { icon: "ri-eye-line", label: "Profile" },
};

export default function OwnerLeads() {
  const { token } = useAuth();
  const [rows, setRows] = useState<OwnerLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"all" | OwnerLead["status"]>("all");
  const [updating, setUpdating] = useState<string | null>(null);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getOwnerLeads(token)
      .then((l) => active && setRows(l))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2200);
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return rows.filter((r) => {
      const matchesStatus = status === "all" || r.status === status;
      const matchesQuery =
        !q ||
        r.name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.businessName.toLowerCase().includes(q) ||
        r.message.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [rows, query, status]);

  const stats = useMemo(
    () => ({
      total: rows.length,
      new: rows.filter((r) => r.status === "new").length,
      won: rows.filter((r) => r.status === "won").length,
    }),
    [rows],
  );

  const updateStatus = async (id: string, next: OwnerLead["status"]) => {
    const previous = rows.find((r) => r.id === id)?.status;
    setUpdating(id);
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: next } : r)));
    const ok = await updateOwnerLeadStatus(id, next, token);
    setUpdating(null);
    if (!ok) {
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: previous ?? r.status } : r)));
      flash("We couldn't update that lead. Please try again.");
      return;
    }
    flash("Lead status updated.");
  };

  const columns: DataColumn<OwnerLead>[] = [
    {
      key: "name",
      header: "Lead",
      render: (r) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-800">
            {r.name.slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="truncate font-semibold text-foreground-950">{r.name}</p>
            <p className="truncate text-xs text-foreground-500">{r.email}</p>
          </div>
        </div>
      ),
    },
    { key: "phone", header: "Phone", nowrap: true, render: (r) => <span className="text-foreground-700">{r.phone}</span> },
    { key: "businessName", header: "Listing", render: (r) => <span className="text-foreground-700">{r.businessName}</span> },
    {
      key: "source",
      header: "Source",
      nowrap: true,
      render: (r) => (
        <span className="inline-flex items-center gap-1 rounded-full bg-background-100 px-2.5 py-1 text-[11px] font-semibold text-foreground-700">
          <i className={sourceMeta[r.source].icon}></i> {sourceMeta[r.source].label}
        </span>
      ),
    },
    { key: "status", header: "Status", nowrap: true, render: (r) => <StatusBadge status={r.status} /> },
    { key: "createdAt", header: "Received", nowrap: true, render: (r) => <span className="text-foreground-500">{r.createdAt}</span> },
    {
      key: "actions",
      header: "Update",
      align: "right",
      nowrap: true,
      render: (r) => (
        <select
          value={r.status}
          disabled={updating === r.id}
          onChange={(e) => updateStatus(r.id, e.target.value as OwnerLead["status"])}
          className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-semibold capitalize text-foreground-700 focus:border-primary-400 focus:outline-none disabled:opacity-50 cursor-pointer"
        >
          {statusOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      ),
    },
  ];

  return (
    <>
      <Seo title="Leads" path="/dashboard/leads" />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950">Leads</h1>
          <p className="mt-1 text-sm text-foreground-600">
            Enquiries and contact events from your listings — work through them to win more customers.
          </p>
        </div>
        {notice && (
          <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
            <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
          </span>
        )}
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: "Total leads", value: stats.total, icon: "ri-inbox-archive-line" },
          { label: "New / unread", value: stats.new, icon: "ri-mail-unread-line" },
          { label: "Won", value: stats.won, icon: "ri-trophy-line" },
        ].map((s) => (
          <div key={s.label} className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className={`${s.icon} text-xl`}></i>
            </span>
            <div>
              <p className="font-heading text-2xl font-extrabold text-foreground-950">{s.value}</p>
              <p className="text-xs text-foreground-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1 w-fit">
          {statusTabs.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setStatus(t.value)}
              className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                status === t.value ? "bg-primary-500 text-foreground-950" : "text-foreground-600 hover:bg-background-100"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search leads…"
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm focus:border-primary-400 focus:outline-none"
          />
        </div>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ) : error ? (
          <EmptyState icon="ri-wifi-off-line" title="Couldn't load leads" description="Please refresh and try again." />
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            minWidth="1080px"
            empty={<p className="text-sm text-foreground-500">No leads match this filter yet.</p>}
          />
        )}
      </div>
    </>
  );
}